import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom'
import UserHoverCard from '../components/ui/UserHoverCard'
import { useGetDiscussionQuery, useVoteDiscussionMutation, useTogglePinDiscussionMutation, useUpdateDiscussionMutation, useFollowDiscussionMutation, useUnfollowDiscussionMutation, useSummariseDiscussionMutation, useGetRelatedDiscussionsQuery, useGetSuggestedAgentsQuery } from '../store/api/discussionsApi'
import { useGetAiUsageQuery } from '../store/api/usersApi'
import { useGetSpendingCapConfigQuery } from '../store/api/agentsApi'
import type { SummaryResult, RelatedDiscussion } from '../store/api/discussionsApi'
import { useAppSelector } from '../hooks/useAppDispatch'
import { agentsApi } from '../store/api/agentsApi'
import { apiSlice } from '../store/api/apiSlice'
import { store } from '../store'
import { useSSE } from '../hooks/useSSE'
import { useLoginPrompt } from '../context/LoginPromptContext'
import { useToast } from '../context/ToastContext'
import { api, getErrorMessage } from '../lib/apiClient'
import { ENDPOINTS } from '../lib/endpoints'
import { DOM_EVENTS, COMMENT_PREVIEW_REPLIES, COMMENTS_PAGE_SIZE, PINNING_ENABLED, REPORTING_ENABLED, DISCUSSION_EDIT_ENABLED, PIN_COLOR, PIN_COLOR_BG, COLOR_SUCCESS, COLOR_ERROR, COLOR_AI_BG, COLOR_AI_BORDER, AI_SUMMARY_ERROR_RESET_MS } from '../lib/config'
import { timeAgoShort, contentPreview } from '../lib/avatar'
import ShareModal from '../components/ui/ShareModal'
import ReportModal from '../components/ui/ReportModal'
import { Modal, Alert, Button, RefreshButton, InlineEditField } from '../components/ui'
import { AISummaryButton } from '../features/discussions/AISummaryButton'
import PollWidget from '../components/poll/PollWidget'
import { useGetPollQuery } from '../store/api/pollsApi'
import Avatar from '../components/ui/Avatar'
import SSEStatusIndicator from '../components/ui/SSEStatusIndicator'
import Tooltip from '../components/ui/Tooltip'
import s from '../styles/pages/DiscussionDetailPage.module.css'
import CommentCard from '../features/comments/CommentCard'
import ReplyComposer from '../features/comments/ReplyComposer'
import type { Comment } from '../features/comments/commentTypes'

const bumpPostCount = () =>
  store.dispatch(
    agentsApi.util.updateQueryData('getForumStats', undefined, (draft) => {
      draft.postCount += 1
    })
  )

const PREVIEW_REPLIES = COMMENT_PREVIEW_REPLIES

type CardEventName = typeof DOM_EVENTS.sseNewReply | typeof DOM_EVENTS.sseLikeUpdate | typeof DOM_EVENTS.sseCommentEdited
function dispatchToCard(parentId: string, eventName: CardEventName, detail: unknown) {
  const el = document.querySelector(`[data-comment-id="${parentId}"]`)
  if (el) el.dispatchEvent(new CustomEvent(eventName, { detail, bubbles: false }))
}

function AISummaryWidget({ discussionId }: { discussionId: string }) {
  const [summarise, { isLoading }] = useSummariseDiscussionMutation()
  const [summary, setSummary] = useState<SummaryResult | null>(null)
  const [error, setError] = useState(false)
  const [open, setOpen] = useState(false)

  const handleSummarise = async () => {
    if (summary) { setOpen(o => !o); return }
    try {
      const res = await summarise(discussionId).unwrap()
      setSummary(res.summary)
      setOpen(true)
    } catch {
      setError(true)
      setTimeout(() => setError(false), AI_SUMMARY_ERROR_RESET_MS)
    }
  }

  return (
    <div className={s.summaryWidgetWrap}>
      <button
        onClick={() => void handleSummarise()}
        disabled={isLoading}
        className={s.summaryBtn}
        style={{ background: open && summary ? 'rgba(124,58,237,0.08)' : 'transparent', cursor: isLoading ? 'not-allowed' : 'pointer', opacity: isLoading ? 0.7 : 1 }}
        onMouseEnter={e => { if (!isLoading) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,58,237,0.08)' }}
        onMouseLeave={e => { if (!open || !summary) (e.currentTarget as HTMLButtonElement).style.background = 'transparent' }}
      >
        {isLoading
          ? <><div className={s.summarySpinner} /> Summarising...</>
          : error
          ? <>⚠️ Failed — try again</>
          : <>{open && summary ? '▲' : '✨'} {open && summary ? 'Hide summary' : 'AI Summary'}</>}
      </button>

      {open && summary && (
        <div className={s.summaryPanel}>
          <style>{`@keyframes fadeIn { from { opacity:0; transform:translateY(-4px) } to { opacity:1; transform:translateY(0) } }`}</style>
          <div className={s.summaryWidgetTitle}>{summary.headline}</div>
          <p className={s.summaryWidgetBody}>{summary.summary}</p>
          {summary.keyPoints.length > 0 && (
            <div className={s.summaryPointsList}>
              {summary.keyPoints.map((pt, i) => (
                <div key={i} className={s.summaryPointItem}>
                  <span className={s.summaryPointArrow}>→</span>
                  <span>{pt}</span>
                </div>
              ))}
            </div>
          )}
          <div className={s.summaryMeta}>
            <span>✨</span> AI-generated summary · may not be 100% accurate
          </div>
        </div>
      )}
    </div>
  )
}

function RelatedDiscussionsWidget({ discussionId, onClose }: { discussionId: string; onClose: () => void }) {
  const { data, isLoading } = useGetRelatedDiscussionsQuery(discussionId)
  const related = data?.related ?? []
  const navigate = useNavigate()

  return (
    <div className={s.relatedPopup}>
      <div className={s.relatedHeader}>
        <span className={s.relatedTitle}>🔗 Related Discussions</span>
        <button onClick={onClose} className={s.relatedClose}>✕</button>
      </div>
      {isLoading && <div className={s.relatedLoading}>Loading...</div>}
      {!isLoading && related.length === 0 && <div className={s.relatedEmpty}>No related discussions found</div>}
      {related.map((d, i) => (
        <div
          key={d.id}
          onClick={() => { navigate(`/discussions/${d.id}`); onClose() }}
          style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.65rem 1rem', borderBottom: i < related.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer', transition: 'background 0.1s' }}
          onMouseEnter={e => (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-elevated)'}
          onMouseLeave={e => (e.currentTarget as HTMLDivElement).style.background = 'transparent'}
        >
          <div style={{ width: 28, height: 28, borderRadius: '7px', flexShrink: 0, background: d.category ? `${d.category.color ?? 'var(--accent-primary)'}18` : 'var(--bg-elevated)', border: `1px solid ${d.category?.color ?? 'var(--border)'}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px' }}>
            {d.category?.icon ?? '💬'}
          </div>
          <div className={s.relatedItemContent}>
            <div className={s.relatedItemTitle}>{d.title}</div>
            <div className={s.relatedItemInfo}>
              {d.category && <span style={{ color: d.category.color ?? 'var(--accent-primary)', fontWeight: 500 }}>{d.category.name}</span>}
              <span>💬 {d.commentCount}</span>
              <span>⬆️ {d.voteScore}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default function DiscussionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, accessToken, user, authLoading } = useAppSelector((s) => ({ isAuthenticated: s.auth.isAuthenticated, accessToken: s.auth.accessToken, user: s.auth.user, authLoading: s.auth.isLoading }))
  const { trigger: promptLogin } = useLoginPrompt()
  const toast = useToast()

  const { data: aiUsageData, refetch: refetchAiUsage } = useGetAiUsageQuery(undefined, { skip: !isAuthenticated })
  const discussionCapReached = isAuthenticated && (aiUsageData?.discussion.remaining ?? 1) === 0
  const refetchAiUsageRef = useRef(refetchAiUsage)
  useEffect(() => { refetchAiUsageRef.current = refetchAiUsage }, [refetchAiUsage])
  const { data: capConfigData } = useGetSpendingCapConfigQuery()
  const capConfig = capConfigData?.config

  const optimisticState = (location.state as any)?.optimistic
  const isPending = id?.startsWith('pending-')

  const [comments, setComments] = useState<Comment[]>([])
  const [commentsLoading, setCommentsLoading] = useState(true)
  const [hasMoreComments, setHasMoreComments] = useState(false)
  const [commentsCursor, setCommentsCursor] = useState<string | null>(null)
  const [loadingMoreComments, setLoadingMoreComments] = useState(false)
  const [showMainComposer, setShowMainComposer] = useState(false)
  const [likedCommentIds, setLikedCommentIds] = useState<Set<string>>(new Set())
  const [liveCommentCount, setLiveCommentCount] = useState<number | null>(null)
  const [liveVoteScore, setLiveVoteScore] = useState<number | null>(null)
  const [userVoteDir, setUserVoteDir] = useState<'up' | 'down' | null>(null)
  const [optimisticFollow, setOptimisticFollow] = useState<boolean | null>(null)
  const [followInFlight, setFollowInFlight] = useState(false)
  const [pinInFlight, setPinInFlight] = useState(false)
  const [voteInFlight, setVoteInFlight] = useState(false)
  const [reportInFlight, setReportInFlight] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [isEditingDiscussion, setIsEditingDiscussion] = useState(false)
  const [editDiscTitle, setEditDiscTitle] = useState('')
  const [editDiscContent, setEditDiscContent] = useState('')
  const [editDiscSaving, setEditDiscSaving] = useState(false)
  const [liveDiscTitle, setLiveDiscTitle] = useState<string | null>(null)
  const [liveDiscContent, setLiveDiscContent] = useState<string | null>(null)
  const [liveDiscEditedAt, setLiveDiscEditedAt] = useState<string | null>(null)
  const [liveCategories, setLiveCategories] = useState<any[] | null>(null)
  const [liveTags, setLiveTags] = useState<any[] | null>(null)
  const [spendingCapModal, setSpendingCapModal] = useState<{ resetAt: string } | null>(null)
  const [aiThinking, setAiThinking] = useState<{ agentId: string; agentName: string; agentIcon: string | null; parentCommentId: string | null }[]>([])
  const isFreshDiscussion = !!(location.state as any)?.optimistic?.fresh
  const [elapsedSecs, setElapsedSecs] = useState(0)
  const [enrichedReceived, setEnrichedReceived] = useState(false)
  const [thinkingReceived, setThinkingReceived] = useState(false)
  const [aiJobFailed, setAiJobFailed] = useState(false)
  const [selectedAgentName, setSelectedAgentName] = useState<string | null>(null)
  const [streamingComments, setStreamingComments] = useState<Map<string, string>>(new Map())
  const [liveSuggestedAgents, setLiveSuggestedAgents] = useState<{ id: string; name: string }[] | null>(null)

  useEffect(() => {
    if (!isFreshDiscussion) return
    const t = setInterval(() => setElapsedSecs(s => s + 1), 1000)
    // Auto-fail after 90s in case ai_error SSE event was missed
    const timeout = setTimeout(() => { setAiJobFailed(true); setAiThinking([]) }, 90_000)
    return () => { clearInterval(t); clearTimeout(timeout) }
  }, [isFreshDiscussion])

  const [showRelated, setShowRelated] = useState(false)
  const relatedRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!showRelated) return
    const handler = (e: MouseEvent) => {
      if (relatedRef.current && !relatedRef.current.contains(e.target as Node)) setShowRelated(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showRelated])

  const loaderRef = useRef<HTMLDivElement>(null)

  const { data, isLoading, isFetching, isError, isUninitialized, refetch } = useGetDiscussionQuery(id ?? '', {
    skip: !id || !!isPending,
    selectFromResult: (result) => {
      if (result.data) return result
      const state = store.getState() as any
      const queries = state?.api?.queries ?? {}
      for (const key of Object.keys(queries)) {
        const entry = queries[key]
        if (entry?.endpointName !== 'getDiscussions' || !entry?.data?.data) continue
        const cached = entry.data.data.find((d: any) => d.id === id)
        if (cached) return { ...result, data: { discussion: cached }, isLoading: false, isFetching: true }
      }
      return result
    },
  })
  const [voteDiscussion] = useVoteDiscussionMutation()
  const [togglePin] = useTogglePinDiscussionMutation()
  const [updateDiscussion] = useUpdateDiscussionMutation()
  const [followDiscussion] = useFollowDiscussionMutation()
  const [unfollowDiscussion] = useUnfollowDiscussionMutation()
  const discussion = data?.discussion
  const isOwner = user?.id === discussion?.authorId

  const { data: suggestedAgentsData } = useGetSuggestedAgentsQuery(id!, { skip: !id || !!isPending || !isOwner })
  const suggestedAgents = (liveSuggestedAgents ?? suggestedAgentsData?.agents ?? []).filter(
    a => !comments.some(c => c.type === 'ai_response' && c.agent?.id === a.id) &&
         !aiThinking.some(t => t.agentId === a.id)
  )
  const suggestedAgentsRef = useRef(suggestedAgents)
  useEffect(() => { suggestedAgentsRef.current = suggestedAgents }, [suggestedAgents])
  const [invitingAgentId, setInvitingAgentId] = useState<string | null>(null)
  const [invitedAgentIds, setInvitedAgentIds] = useState<Set<string>>(new Set())

  const handleInviteAgent = async (agentId: string, agentName: string) => {
    if (!isAuthenticated) { promptLogin('Sign in to invite experts'); return }
    if (!isOwner) { toast.error('Only the discussion owner can invite agents'); return }
    if (invitingAgentId || invitedAgentIds.has(agentId)) return
    setInvitingAgentId(agentId)
    try {
      await api.post('discussions', ENDPOINTS.discussions.invokeAgent(id!), { agentId }, accessToken)
      setInvitedAgentIds(prev => new Set([...prev, agentId]))
      toast.success(`${agentName} is generating a response!`)
    } catch (err: any) {
      if (err?.status === 409) {
        toast.info(`${agentName} has already responded to this discussion`)
        setInvitedAgentIds(prev => new Set([...prev, agentId]))
      } else {
        toast.error('Failed to invite agent. Please try again.')
      }
    } finally { setInvitingAgentId(null) }
  }

  const { data: pollData } = useGetPollQuery(id!, { skip: !id || !!isPending })
  const poll = pollData?.poll ?? null

  useEffect(() => {
    if (discussion?.categories && (liveCategories === null || liveCategories.length === 0)) {
      setLiveCategories(discussion.categories)
    }
  }, [discussion?.categories]) // eslint-disable-line react-hooks/exhaustive-deps

  const showOptimistic = !!optimisticState && !discussion && !!isPending
  const hasAiReplies = comments.some((c) => c.type === 'ai_response')
  const canPin = PINNING_ENABLED
  const serverPinned = Array.isArray(discussion?.pins) ? discussion!.pins.length > 0 : false
  const [optimisticPin, setOptimisticPin] = useState<boolean | null>(null)
  const effectivePinned = optimisticPin ?? serverPinned
  const serverFollowing = Array.isArray(discussion?.followers) ? discussion!.followers.length > 0 : false
  const effectiveFollowing = optimisticFollow ?? serverFollowing

  useEffect(() => {
    if (discussion) setUserVoteDir((discussion as any).userVote ?? null)
  }, [discussion?.id])

  useEffect(() => { setOptimisticPin(null) }, [serverPinned])
  useEffect(() => { setOptimisticFollow(null) }, [serverFollowing])

  const handleTogglePin = async () => {
    if (!isAuthenticated) { promptLogin('Sign in to pin discussions'); return }
    if (pinInFlight) return
    setPinInFlight(true)
    setOptimisticPin(!effectivePinned)
    try { await togglePin(id!).unwrap(); void refetch() }
    catch { setOptimisticPin(null) }
    finally { setPinInFlight(false) }
  }

  const handleToggleFollow = async () => {
    if (!isAuthenticated) { promptLogin('Sign in to follow discussions'); return }
    if (followInFlight) return
    setFollowInFlight(true)
    const isFollowing = effectiveFollowing
    setOptimisticFollow(!isFollowing)
    try {
      if (isFollowing) { await unfollowDiscussion(id!).unwrap(); toast.info('Unfollowed discussion') }
      else { await followDiscussion(id!).unwrap(); toast.success("Following — you'll be notified of new replies") }
      void refetch()
    } catch {
      setOptimisticFollow(isFollowing)
      toast.error('Failed to update follow status')
    } finally { setFollowInFlight(false) }
  }

  const fetchComments = useCallback(async (cursor?: string) => {
    if (!id) return
    try {
      const params = new URLSearchParams({ limit: String(COMMENTS_PAGE_SIZE) })
      if (cursor) params.set('cursor', cursor)
      const d = await api.get<{ data: Comment[]; hasMore: boolean; nextCursor: string | null }>('comments', `${ENDPOINTS.comments.list(id)}?${params}`, accessToken)
      setComments((prev) => cursor ? [...prev, ...d.data] : d.data)
      setHasMoreComments(d.hasMore)
      setCommentsCursor(d.nextCursor)
    } catch {}
  }, [id, accessToken])

  useEffect(() => {
    if (!id) return
    // Wait for the initial session check to resolve first — otherwise a hard
    // refresh fires this before accessToken is restored, so the server can't
    // tell who's viewing and premium comments this user already unlocked would
    // come back locked. Once authLoading flips to false (exactly once), we
    // re-run with whatever accessToken is now available.
    if (authLoading) return
    setCommentsLoading(true)
    setComments([])
    setCommentsCursor(null)
    const params = new URLSearchParams({ limit: String(COMMENTS_PAGE_SIZE) })
    api.get<{ data: Comment[]; hasMore: boolean; nextCursor: string | null }>('comments', `${ENDPOINTS.comments.list(id)}?${params}`, accessToken)
      .then((d) => { setComments(d.data); setHasMoreComments(d.hasMore); setCommentsCursor(d.nextCursor) })
      .catch(() => {})
      .finally(() => setCommentsLoading(false))
    if (!isAuthenticated) window.dispatchEvent(new Event(DOM_EVENTS.guestInteraction))
  }, [id, authLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isAuthenticated || !accessToken) return
    api.get<{ ids: string[] }>('comments', `${ENDPOINTS.comments.liked}?discussionId=${id}`, accessToken)
      .then((d) => setLikedCommentIds(new Set(d.ids))).catch(() => {})
  }, [isAuthenticated, accessToken])

  useEffect(() => {
    if (!loaderRef.current || !hasMoreComments || loadingMoreComments) return
    const el = loaderRef.current
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) {
        setLoadingMoreComments(true)
        fetchComments(commentsCursor ?? undefined).finally(() => setLoadingMoreComments(false))
      }
    }, { threshold: 0.1 })
    obs.observe(el)
    return () => obs.disconnect()
  }, [hasMoreComments, loadingMoreComments, commentsCursor, fetchComments])

  const { status: sseStatus } = useSSE({
    discussionId: id, enabled: isAuthenticated && !!id,
    onEvent: {
      ai_thinking: (data: unknown) => {
        const d = data as { discussionId: string; agentId: string; agentName: string; agentIcon: string | null; parentCommentId: string | null }
        if (d.discussionId !== id) return
        setThinkingReceived(true)
        setAiJobFailed(false)
        setAiThinking(prev => prev.some(t => t.agentId === d.agentId) ? prev : [...prev, { agentId: d.agentId, agentName: d.agentName, agentIcon: d.agentIcon, parentCommentId: d.parentCommentId }])
        setStreamingComments(prev => { const next = new Map(prev); next.set(d.agentId, ''); return next })
      },
      ai_token: (data: unknown) => {
        const d = data as { discussionId: string; agentId: string; token: string }
        if (d.discussionId !== id) return
        setStreamingComments(prev => {
          const next = new Map(prev)
          next.set(d.agentId, (next.get(d.agentId) ?? '') + d.token)
          return next
        })
      },
      ai_complete: (data: unknown) => {
        const d = data as { discussionId: string; commentId: string; agentId: string; agentName: string; agentIcon: string | null; content: string; createdAt: string; parentCommentId: string | null }
        if (d.discussionId !== id) return
        const aiComment = { id: d.commentId, content: d.content, type: 'ai_response', createdAt: d.createdAt, depth: d.parentCommentId ? 1 : 0, voteScore: 0, replyCount: 0, parentId: d.parentCommentId, agent: { id: d.agentId, name: d.agentName } }
        if (d.parentCommentId) {
          dispatchToCard(d.parentCommentId, DOM_EVENTS.sseNewReply, aiComment)
        } else {
          setComments((prev) => prev.some((c) => c.id === d.commentId) ? prev : [aiComment, ...prev])
          setLiveCommentCount((prev) => (prev ?? (discussion?.commentCount ?? 0)) + 1)
        }
        setLiveSuggestedAgents(suggestedAgentsRef.current.filter(a => a.id !== d.agentId))
        setInvitedAgentIds(prev => { const next = new Set(prev); next.delete(d.agentId); return next })
        setStreamingComments(prev => { const next = new Map(prev); next.delete(d.agentId); return next })
        setAiThinking(prev => prev.filter(t => t.agentId !== d.agentId))
      },
      ai_error: (data: unknown) => {
        const d = data as { discussionId: string; code?: string; resetAt?: string }
        if (d.discussionId !== id) return
        if (d.code === 'SPENDING_CAP_REACHED' && d.resetAt) setSpendingCapModal({ resetAt: d.resetAt })
        setAiThinking([])
        setThinkingReceived(false)
        setAiJobFailed(true)
      },
      ai_usage_updated: () => { void refetchAiUsageRef.current() },
      new_comment: (data: unknown) => {
        const d = data as Comment & { discussionId: string }
        if (d.discussionId !== id) return
        if (d.authorId && d.authorId === user?.id) return
        if (d.parentId) { dispatchToCard(d.parentId, DOM_EVENTS.sseNewReply, d) }
        else { setComments((prev) => prev.some((c) => c.id === d.id) ? prev : [d, ...prev]) }
        setLiveCommentCount((prev) => (prev ?? (discussion?.commentCount ?? 0)) + 1)
        // An internal agent's reply arrives via ai_complete, which clears its own
        // "thinking" entry. An external agent's reply arrives here instead — it
        // never streams, so there is no ai_complete for it — and without this,
        // the thinking indicator for that agent would sit next to its own answer
        // forever, since nothing else ever removes it.
        if (d.type === 'ai_response' && d.agent?.id) {
          const agentId = d.agent.id
          setAiThinking((prev) => prev.filter((t) => t.agentId !== agentId))
          setStreamingComments((prev) => { const next = new Map(prev); next.delete(agentId); return next })
        }
      },
      content_removed: (data: unknown) => {
        const d = data as { targetType: 'discussion' | 'comment'; targetId: string; title?: string | null; discussionId?: string }
        if (d.targetType === 'discussion' && d.targetId === id) {
          store.dispatch(apiSlice.util.invalidateTags(['Discussion']))
          void navigate('/', { replace: true, state: { removed: true, removedTitle: d.title ?? null } })
        } else if (d.targetType === 'comment') {
          setComments((prev) => prev.filter((c) => c.id !== d.targetId))
        }
      },
      vote_updated: (data: unknown) => { const d = data as { discussionId: string; voteScore: number }; if (d.discussionId !== id) return; setLiveVoteScore(d.voteScore) },
      comment_liked: (data: unknown) => { const d = data as { commentId: string; likeCount: number; userId: string }; if (d.userId === user?.id) return; dispatchToCard(d.commentId, DOM_EVENTS.sseLikeUpdate, { likeCount: d.likeCount }) },
      comment_edited: (data: unknown) => { const d = data as { commentId: string; content: string; editedAt: string }; dispatchToCard(d.commentId, DOM_EVENTS.sseCommentEdited, d) },
      discussion_updated: (data: unknown) => {
        const d = data as { discussionId: string; title: string; content: string; editedAt: string | null }
        if (d.discussionId !== id) return
        setLiveDiscTitle(d.title); setLiveDiscContent(d.content); setLiveDiscEditedAt(d.editedAt)
      },
      discussion_enriched: (data: unknown) => {
        const d = data as { discussionId: string; categories: any[]; tags: any[]; agentNames?: string[]; suggestedAgents?: { id: string; name: string }[] }
        if (d.discussionId !== id) return
        setEnrichedReceived(true)
        if (d.agentNames?.length) setSelectedAgentName(d.agentNames[0]!)
        if (d.suggestedAgents?.length) setLiveSuggestedAgents(d.suggestedAgents)
        setLiveCategories(d.categories); setLiveTags(d.tags)
      },
      category_updated: (data: unknown) => {
        const d = data as { discussionId: string; category: any }
        if (d.discussionId !== id) return
        setLiveCategories((prev) => {
          const current = prev ?? []
          if (current.some((c: any) => c.id === d.category.id)) return current
          return [...current, d.category]
        })
      },
    },
  })

  const handlePostComment = async (content: string) => {
    try {
      const d = await api.post<{ comment: Comment }>('comments', ENDPOINTS.comments.create(id!), { content }, accessToken)
      setComments((prev) => [d.comment, ...prev])
      setLiveCommentCount((prev) => (prev ?? (discussion?.commentCount ?? 0)) + 1)
      setAiThinking([])
      bumpPostCount()
      setShowMainComposer(false)
      void refetch()
    } catch (err: any) {
      toast.error(getErrorMessage(err, 'Failed to post reply. Please try again.'))
    }
  }

  const handleVote = async (dir: 'up' | 'down') => {
    if (!isAuthenticated) { promptLogin('Sign in to vote'); return }
    if (voteInFlight) return
    const prevScore = liveVoteScore ?? discussion?.voteScore ?? 0
    const prevDir = userVoteDir
    if (prevDir === dir) { setLiveVoteScore(dir === 'up' ? prevScore - 1 : prevScore + 1); setUserVoteDir(null) }
    else if (prevDir !== null) { setLiveVoteScore(dir === 'up' ? prevScore + 2 : prevScore - 2); setUserVoteDir(dir) }
    else { setLiveVoteScore(dir === 'up' ? prevScore + 1 : prevScore - 1); setUserVoteDir(dir) }
    setVoteInFlight(true)
    try {
      await voteDiscussion({ id: id!, direction: dir }).unwrap()
      toast.success(prevDir === dir ? 'Vote removed' : dir === 'up' ? 'Upvoted!' : 'Downvoted!')
      void refetch()
    } catch (err: any) {
      setLiveVoteScore(prevScore); setUserVoteDir(prevDir)
      toast.error(getErrorMessage(err, 'Failed to vote. Please try again.'))
    } finally { setVoteInFlight(false) }
  }

  const handleSaveDiscussionEdit = async () => {
    if (editDiscSaving) return
    if (!editDiscTitle.trim()) { toast.error('Title is required'); return }
    if (editDiscTitle.trim().length < 10) { toast.error('Title must be at least 10 characters'); return }
    if (!editDiscContent.trim()) { toast.error('Content is required'); return }
    if (editDiscContent.trim().length < 20) { toast.error('Content must be at least 20 characters'); return }
    setEditDiscSaving(true)
    try {
      await updateDiscussion({ id: id!, body: { title: editDiscTitle.trim(), content: editDiscContent.trim() } }).unwrap()
      void refetch()
      setIsEditingDiscussion(false)
    } catch (err) { toast.error(getErrorMessage(err, 'Failed to save changes. Please try again.')) }
    setEditDiscSaving(false)
  }

  useEffect(() => {
    if (discussion && optimisticState) window.history.replaceState({}, '')
  }, [discussion?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Deep-link: load all comments + replies then scroll to target ──
  const locationState = location.state as { targetCommentId?: string | null; parentCommentId?: string | null } | null
  const targetCommentId = locationState?.targetCommentId ?? null
  const targetParentCommentId = locationState?.parentCommentId ?? null
  const deepLinkHandledRef = useRef<string | null>(null)

  useEffect(() => {
    if (!targetCommentId || commentsLoading) return
    if (deepLinkHandledRef.current === targetCommentId) return
    deepLinkHandledRef.current = targetCommentId

    const run = async () => {
      // Load all top-level comments in one shot
      try {
        const d = await api.get<{ data: Comment[]; hasMore: boolean; nextCursor: string | null }>(
          'comments', `${ENDPOINTS.comments.list(id!)}?limit=500`, accessToken
        )
        setComments(d.data)
        setHasMoreComments(d.hasMore)
        setCommentsCursor(d.nextCursor)
      } catch { return }

      // Give React a tick to render all comments
      await new Promise((r) => setTimeout(r, 80))

      // If target is a reply, the parent CommentCard will auto-load all its replies
      // (handleOpenReplies now fetches limit=500). Just wait for that then scroll.
      if (targetParentCommentId) {
        const parentEl = document.querySelector(`[data-comment-id="${targetParentCommentId}"]`)
        if (parentEl) {
          // Trigger open replies on the parent card
          parentEl.dispatchEvent(new CustomEvent(DOM_EVENTS.deeplinkOpenReplies, { bubbles: false }))
          // Wait for replies to load and render
          await new Promise((r) => setTimeout(r, 400))
        }
      }

      const target = document.querySelector(`[data-comment-id="${targetCommentId}"]`)
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    void run()
  }, [commentsLoading, targetCommentId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isFreshDiscussion || commentsLoading || !id) return
    const interval = setInterval(() => {
      if (comments.length === 0 && aiThinking.length === 0) void fetchComments()
      else clearInterval(interval)
    }, 10000)
    return () => clearInterval(interval)
  }, [isFreshDiscussion, commentsLoading, id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showOptimistic || isPending || isUninitialized || isLoading || isFetching) return
    if (isError || !discussion) void navigate('/', { replace: true, state: { removed: true } })
  }, [showOptimistic, isPending, isUninitialized, isLoading, isFetching, isError, discussion])

  if (showOptimistic && optimisticState) {
    return (
      <div className={s.pageWrap}>
        <button onClick={() => navigate(-1)} className={s.backBtn}>← Back</button>
        <div className={s.optimisticCard}>
          <h1 className={s.discussionTitle}>{optimisticState.title}</h1>
          <div className={s.discussionBody}>{optimisticState.content}</div>
          {(optimisticState.tags ?? []).length > 0 && (
            <div className={s.discussionTagsRow}>
              {(optimisticState.tags ?? []).map((t: string) => (
                <span key={t}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); void navigate(`/search?tag=${encodeURIComponent(t)}`) }}
                  style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', padding: '0.1rem 0.45rem', borderRadius: '6px', fontSize: '0.68rem', border: '1px solid var(--border)', cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={(e) => { const el = e.currentTarget as HTMLSpanElement; el.style.color = 'var(--accent-primary)'; el.style.borderColor = 'rgba(124,58,237,0.4)'; el.style.background = 'rgba(124,58,237,0.08)' }}
                  onMouseLeave={(e) => { const el = e.currentTarget as HTMLSpanElement; el.style.color = 'var(--text-muted)'; el.style.borderColor = 'var(--border)'; el.style.background = 'var(--bg-elevated)' }}
                >#{t}</span>
              ))}
            </div>
          )}
          <div className={s.discussionFooter}>
            <div className={s.aiLiveIndicator}>
              <div className={s.pulseDot} />
              Publishing your discussion...
            </div>
          </div>
          <style>{`@keyframes pulse { 0%,100%{opacity:0.4} 50%{opacity:1} }`}</style>
        </div>
        <div className={s.aiAssignedBanner}>
          <div className={s.aiAssignedIcon}>🤖</div>
          <div>
            <div className={s.aiAssignedTitle}>AI agents are being assigned...</div>
            <div className={s.aiAssignedSub}>Relevant experts will respond automatically once your discussion is live.</div>
          </div>
        </div>
      </div>
    )
  }

  if (!showOptimistic && !optimisticState && (isLoading || isUninitialized || isFetching) && !discussion) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh', color: 'var(--text-muted)' }}>Loading...</div>
  if (isError || (!discussion && !optimisticState)) return null

  const o = optimisticState
  const commentCount = liveCommentCount ?? discussion?.commentCount ?? o?.commentCount ?? 0
  const voteScore = liveVoteScore ?? discussion?.voteScore ?? o?.voteScore ?? 0
  const authorName = discussion?.author.profile?.displayName ?? o?.author?.displayName ?? 'User'
  const authorAvatarUrl = discussion
    ? (discussion.author.id === user?.id ? (user.avatarUrl || discussion.author.profile?.avatarUrl) : discussion.author.profile?.avatarUrl)
    : o?.author?.avatarUrl ?? null
  const displayTitle = liveDiscTitle ?? discussion?.title ?? o?.title ?? ''
  const displayContent = liveDiscContent ?? discussion?.content ?? o?.content ?? ''
  const displayEditedAt = liveDiscEditedAt !== null ? liveDiscEditedAt : (discussion as any)?.editedAt

  return (
    <div className={s.pageWrap}>
      <button
        onClick={() => navigate(-1)}
        className={s.backBtn}
        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '1' }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.8' }}
      >
        ← Back
      </button>

      <div className={s.optimisticCard}>
        <div className={s.discussionMetaRow}>
          <div className={s.metaLeft}>
            <span className={s.metaItem}>👁 {discussion?.viewCount ?? o?.viewCount ?? 0}</span>
            <span className={s.metaItem}>{timeAgoShort(discussion?.createdAt ?? o?.createdAt ?? '')} ago</span>
          </div>
          {id && (
            <div ref={relatedRef} className={s.relatedBtnWrap}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AISummaryButton discussionId={id} variant="inline" />
                <button
                  onClick={() => setShowRelated(v => !v)}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.28rem 0.65rem', borderRadius: '8px', border: `1px solid ${showRelated ? 'var(--accent-primary)' : 'var(--border)'}`, background: showRelated ? 'rgba(124,58,237,0.08)' : 'transparent', color: showRelated ? 'var(--accent-primary)' : 'var(--text-muted)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: showRelated ? 600 : 400, transition: 'all 0.15s' }}
                  onMouseEnter={e => { if (!showRelated) { (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent-primary)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--accent-primary)' } }}
                  onMouseLeave={e => { if (!showRelated) { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)' } }}
                >
                  🔗 Related Discussion
                </button>
                {showRelated && (
                  <>
                    <div className={s.mobileBackdrop} onClick={() => setShowRelated(false)} />
                    <RelatedDiscussionsWidget discussionId={id} onClose={() => setShowRelated(false)} />
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        <h1 className={s.discussionTitle}>{displayTitle}</h1>

        {isEditingDiscussion ? (
          <div className={s.editFieldSection}>
            <InlineEditField
              title={editDiscTitle}
              onTitleChange={setEditDiscTitle}
              titleMin={10}
              titleMax={300}
              titlePlaceholder="Title"
              body={editDiscContent}
              onBodyChange={setEditDiscContent}
              bodyMin={20}
              bodyRows={5}
              bodyPlaceholder="Content"
              onSave={() => void handleSaveDiscussionEdit()}
              onCancel={() => setIsEditingDiscussion(false)}
              saving={editDiscSaving}
            />
          </div>
        ) : (
          <>
            <div className={s.discussionBody}>{displayContent}</div>
            {displayEditedAt && <div className={s.discussionEdited}>(edited)</div>}
          </>
        )}

        {(() => {
          const displayCategories = liveCategories ?? discussion?.categories ?? o?.categories ?? []
          const displayTags = liveTags ?? discussion?.tags ?? o?.tags ?? []
          if (displayCategories.length === 0 && displayTags.length === 0) {
            if (!discussion) return null
            return (
              <div className={s.discussionTagsRow}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', background: 'var(--bg-elevated)', border: '1px solid var(--border)', padding: '0.2rem 0.55rem', borderRadius: '8px' }}>
                  ⏳ Classifying...
                </span>
              </div>
            )
          }
          return (
            <div className={s.discussionTagsRow}>
              {displayCategories.map((cat: any) => (
                <span key={cat.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', fontWeight: 600, color: cat.color ?? 'var(--accent-primary)', background: `${cat.color ?? 'var(--accent-primary)'}18`, border: `1px solid ${cat.color ?? 'var(--accent-primary)'}44`, padding: '0.2rem 0.55rem', borderRadius: '8px', transition: 'all 0.2s' }}>
                  {cat.icon && <span>{cat.icon}</span>}
                  {cat.name}
                </span>
              ))}
              {displayCategories.length > 0 && displayTags.length > 0 && <span className={s.catDot}>·</span>}
              {displayTags.map((t: any) => {
                const slug = t.tag?.slug ?? t.slug ?? t.tag?.name ?? t.name
                const name = t.tag?.name ?? t.name
                return (
                  <span key={t.tag?.id ?? t.id}
                    onClick={() => void navigate(`/search?tag=${encodeURIComponent(slug)}`)}
                    style={{ background: 'var(--bg-elevated)', color: 'var(--text-muted)', padding: '0.1rem 0.45rem', borderRadius: '6px', fontSize: '0.68rem', border: '1px solid var(--border)', cursor: 'pointer', transition: 'all 0.15s' }}
                    onMouseEnter={(e) => { const el = e.currentTarget as HTMLSpanElement; el.style.color = 'var(--accent-primary)'; el.style.borderColor = 'rgba(124,58,237,0.4)'; el.style.background = 'rgba(124,58,237,0.08)' }}
                    onMouseLeave={(e) => { const el = e.currentTarget as HTMLSpanElement; el.style.color = 'var(--text-muted)'; el.style.borderColor = 'var(--border)'; el.style.background = 'var(--bg-elevated)' }}
                  >#{name}</span>
                )
              })}
            </div>
          )
        })()}

        <div className={s.authorRow}>
          <UserHoverCard userId={discussion?.author.id ?? o?.author?.id ?? ''} currentUserId={user?.id} displayName={authorName} avatarUrl={authorAvatarUrl}>
            <Avatar name={authorName} avatarUrl={authorAvatarUrl} size={22} />
          </UserHoverCard>
          <UserHoverCard userId={discussion?.author.id ?? o?.author?.id ?? ''} currentUserId={user?.id} displayName={authorName} avatarUrl={authorAvatarUrl}>
            <span className={s.authorByline}
              onMouseEnter={(e) => ((e.currentTarget as HTMLSpanElement).style.color = 'var(--accent-primary)')}
              onMouseLeave={(e) => ((e.currentTarget as HTMLSpanElement).style.color = 'var(--text-muted)')}
            >
              <strong>{authorName}</strong>
            </span>
          </UserHoverCard>
          {discussion?.author.profile?.flair && (
            <span style={{ fontSize: '0.68rem', background: 'rgba(124,58,237,0.08)', color: 'var(--accent-primary)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: '20px', padding: '0.08rem 0.45rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
              {discussion.author.profile.flair}
            </span>
          )}
        </div>

        <div className={s.discussionFooter}>
          <Tooltip text={isOwner ? "You can't vote on your own discussion" : ''}>
            <button onClick={() => void handleVote('up')} disabled={isOwner || voteInFlight}
              style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.85rem', borderRadius: '20px', border: `1px solid ${isOwner ? 'var(--border)' : userVoteDir === 'up' ? 'var(--accent-primary)' : 'var(--border)'}`, background: isOwner ? 'transparent' : userVoteDir === 'up' ? 'var(--accent-light)' : 'transparent', color: isOwner ? 'var(--text-muted)' : userVoteDir === 'up' ? 'var(--accent-primary)' : 'var(--text-muted)', cursor: isOwner || voteInFlight ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: userVoteDir === 'up' ? 600 : 400, transition: 'all 0.15s', opacity: isOwner ? 0.45 : voteInFlight ? 0.6 : 1 }}>
              ↑ {voteScore} Upvotes
            </button>
          </Tooltip>
          <button onClick={() => setShowShareModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.85rem', borderRadius: '20px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.78rem', transition: 'color 0.15s' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent-primary)' }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}
          >
            <span>🔗</span><span>Share</span>
          </button>
          {isAuthenticated && (
            <button onClick={() => void handleToggleFollow()} disabled={followInFlight}
              style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.85rem', borderRadius: '20px', border: `1px solid ${effectiveFollowing ? 'var(--accent-primary)' : 'var(--border)'}`, background: effectiveFollowing ? 'var(--accent-light)' : 'transparent', color: effectiveFollowing ? 'var(--accent-primary)' : 'var(--text-muted)', cursor: followInFlight ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: effectiveFollowing ? 600 : 400, transition: 'all 0.15s', opacity: followInFlight ? 0.5 : 1, pointerEvents: followInFlight ? 'none' : 'auto' }}>
              {effectiveFollowing ? '🔔 Following' : '🔔 Follow'}
            </button>
          )}
          {canPin && (
            <button onClick={() => void handleTogglePin()} disabled={pinInFlight}
              style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.85rem', borderRadius: '20px', border: `1px solid ${effectivePinned ? PIN_COLOR : 'var(--border)'}`, background: effectivePinned ? PIN_COLOR_BG : 'transparent', color: effectivePinned ? PIN_COLOR : 'var(--text-muted)', cursor: pinInFlight ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: effectivePinned ? 600 : 400, transition: 'all 0.15s', opacity: pinInFlight ? 0.5 : 1, pointerEvents: pinInFlight ? 'none' : 'auto' }}>
              📌 {effectivePinned ? 'Pinned' : 'Pin'}
            </button>
          )}
          {DISCUSSION_EDIT_ENABLED && isOwner && !isEditingDiscussion && (
            <Tooltip text={hasAiReplies ? 'Cannot edit after AI has replied' : ''}>
              <button
                onClick={() => { if (hasAiReplies) return; setEditDiscTitle(displayTitle); setEditDiscContent(contentPreview(displayContent, Infinity)); setIsEditingDiscussion(true) }}
                disabled={hasAiReplies}
                style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.85rem', borderRadius: '20px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: hasAiReplies ? 'not-allowed' : 'pointer', fontSize: '0.78rem', transition: 'all 0.15s', opacity: hasAiReplies ? 0.4 : 1, pointerEvents: hasAiReplies ? 'none' : 'auto' }}
                onMouseEnter={(e) => { if (!hasAiReplies) { (e.currentTarget as HTMLButtonElement).style.color = 'var(--accent-primary)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--accent-primary)' } }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)' }}
              >
                {hasAiReplies ? '🔒' : '✏️'} Edit
              </button>
            </Tooltip>
          )}
          {REPORTING_ENABLED && user?.id !== (discussion?.author.id ?? o?.author?.id) && (
            <button
              disabled={reportInFlight}
              onClick={() => { if (!isAuthenticated) { promptLogin('Sign in to report content'); return }; if (reportInFlight) return; setReportInFlight(true); setShowReportModal(true) }}
              style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.85rem', borderRadius: '20px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', cursor: reportInFlight ? 'not-allowed' : 'pointer', fontSize: '0.78rem', transition: 'all 0.15s', opacity: reportInFlight ? 0.5 : 1, pointerEvents: reportInFlight ? 'none' : 'auto' }}
              onMouseEnter={(e) => { if (!reportInFlight) { (e.currentTarget as HTMLButtonElement).style.color = COLOR_ERROR; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(239,68,68,0.4)' } }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--border)' }}
            >
              🚩 Report
            </button>
          )}
          <div className={s.footerRight}>
            <button
              onClick={() => { if (!isAuthenticated) { promptLogin('Sign in to reply'); return }; setShowMainComposer(!showMainComposer) }}
              style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', padding: '0.4rem 1rem', borderRadius: '20px', background: 'var(--accent-primary)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.8rem', opacity: 1 }}>
              ↩ Reply
            </button>
          </div>
        </div>

        {showMainComposer && (
          <div className={s.composerWrap}>
            <ReplyComposer onSubmit={handlePostComment} onCancel={() => setShowMainComposer(false)} placeholder="Write your reply..." discussionId={id} />
          </div>
        )}
      </div>

      {showReportModal && id && (
        <ReportModal targetType="discussion" targetId={id} onClose={() => { setShowReportModal(false); setReportInFlight(false) }} />
      )}

      {showShareModal && (
        <ShareModal
          title={discussion?.title ?? ''}
          contentPreview={discussion?.content ? discussion.content.slice(0, 120) : ''}
          url={window.location.href}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {poll && id && <PollWidget poll={poll} isAuthor={user?.id === discussion?.authorId} />}

      {(() => {
        const agentMap = new Map<string, { id: string; name: string }>()
        const collect = (commentList: typeof comments) => {
          for (const c of commentList) {
            if (c.type === 'ai_response' && c.agent?.id) agentMap.set(c.agent.id, c.agent)
            if (c.replies?.length) collect(c.replies)
          }
        }
        collect(comments)
        const involvedAgents = [...agentMap.values()]
        const hasAnything = involvedAgents.length > 0 || suggestedAgents.length > 0 || id
        if (!hasAnything) return null
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', margin: '0.4rem 0', padding: '0.35rem 0.6rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '10px' }}>
            {involvedAgents.length > 0 && (
              <>
                <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', flexShrink: 0 }}>🤖</span>
                {involvedAgents.map(agent => (
                  <span key={agent.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-card)', border: '1px solid var(--border)', padding: '0.1rem 0.45rem', borderRadius: '20px' }}>
                    <span style={{ fontSize: '0.65rem' }}>{'🤖'}</span>{agent.name}
                  </span>
                ))}
                {suggestedAgents.length > 0 && <span style={{ color: 'var(--border)', fontSize: '0.7rem' }}>·</span>}
              </>
            )}
            {suggestedAgents.length > 0 && isOwner && (
              <>
                <span style={{ color: 'var(--border)', fontSize: '0.7rem' }}>·</span>
                <span style={{ fontSize: '0.65rem', color: discussionCapReached ? 'var(--text-muted)' : 'var(--accent-primary)', fontWeight: 700, flexShrink: 0 }}>💡</span>
                {suggestedAgents.map(agent => {
                  const invited = invitedAgentIds.has(agent.id)
                  const inviting = invitingAgentId === agent.id
                  const blocked = discussionCapReached && !invited
                  return (
                    <Tooltip key={agent.id} text={blocked ? 'Daily AI limit reached' : ''}>
                      <button onClick={() => !blocked && void handleInviteAgent(agent.id, agent.name)} disabled={invited || !!invitingAgentId || blocked}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', padding: '0.1rem 0.45rem', borderRadius: '20px', border: `1px solid ${invited ? 'rgba(34,197,94,0.4)' : blocked ? 'var(--border)' : 'rgba(124,58,237,0.3)'}`, background: invited ? 'rgba(34,197,94,0.07)' : blocked ? 'transparent' : 'rgba(124,58,237,0.07)', color: invited ? COLOR_SUCCESS : blocked ? 'var(--text-muted)' : 'var(--accent-primary)', fontSize: '0.72rem', fontWeight: 600, cursor: invited || !!invitingAgentId || blocked ? 'not-allowed' : 'pointer', opacity: (!!invitingAgentId && !inviting) || blocked ? 0.5 : 1, transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: '0.65rem' }}>{'🤖'}</span>
                        {agent.name}
                        {!invited && !inviting && !blocked && <span style={{ fontSize: '0.6rem', opacity: 0.6 }}>→</span>}
                        {inviting && <span style={{ fontSize: '0.6rem' }}>...</span>}
                        {invited && <span style={{ fontSize: '0.6rem' }}>✓</span>}
                        {blocked && <span style={{ fontSize: '0.6rem' }}>⚡</span>}
                      </button>
                    </Tooltip>
                  )
                })}
              </>
            )}
            {isAuthenticated && <span style={{ marginLeft: 'auto' }}><SSEStatusIndicator status={sseStatus} /></span>}
          </div>
        )
      })()}

      <div className={s.commentsHeaderRow}>
        <span className={s.commentsTitle}>
          {commentsLoading ? '...' : `${comments.length}${hasMoreComments ? '+' : ''} ${comments.length === 1 ? 'Reply' : 'Replies'}`}
        </span>
        <RefreshButton
          onClick={() => { setCommentsCursor(null); void fetchComments() }}
          loading={commentsLoading}
          title="Refresh replies"
        />
      </div>

      {commentsLoading && [1, 2, 3].map((i) => (
        <div key={i} className={s.commentSkeleton}>
          <div className={s.commentSkeletonAvatar} />
          <div className={s.commentSkeletonBody}>
            <div className={s.skelLine} style={{ width: '25%' }} />
            <div className={s.skelLine} style={{ width: `${60 + i * 10}%` }} />
            <div className={s.skelLine} style={{ width: '40%' }} />
          </div>
        </div>
      ))}

      {!commentsLoading && comments.length === 0 && aiThinking.length === 0 && !aiJobFailed && (
        isFreshDiscussion ? (
          <div className={s.aiWarmupCard}>
            <div className={s.aiWarmupGradient} />
            <style>{`@keyframes shimmerBar { 0%{background-position:200% 0} 100%{background-position:-200% 0} }`}</style>
            <div className={s.aiWarmupPadding}>
              <div className={s.aiWarmupHeader}>
                <div className={s.aiWarmupIconBox}>{aiJobFailed ? '⚠️' : '🤖'}</div>
                <div>
                  <div className={s.aiWarmupTitle}>{aiJobFailed ? 'Agent failed to respond' : 'AI agent is warming up...'}</div>
                  <div className={s.aiWarmupSub}>{aiJobFailed ? 'Something went wrong generating a response. Try posting a reply to retry.' : 'Analysing your question and preparing a response'}</div>
                </div>
                {!aiJobFailed && <div className={s.aiWarmupTimer}>{elapsedSecs}s</div>}
              </div>
              {!aiJobFailed && (() => {
                const steps: { label: string; done: boolean; active: boolean; icon: string }[] = [
                  { icon: '💬', label: 'Question received', done: true, active: false },
                  { icon: '🔍', label: 'Analysing topic & intent', done: enrichedReceived, active: !enrichedReceived },
                  { icon: '🏷️', label: enrichedReceived ? 'Categories & tags assigned' : 'Assigning categories & tags', done: enrichedReceived, active: !enrichedReceived },
                  { icon: '🤖', label: enrichedReceived && selectedAgentName ? `${selectedAgentName} selected` : 'Selecting the right agent', done: enrichedReceived, active: !enrichedReceived },
                  { icon: '✏️', label: thinkingReceived ? `${selectedAgentName ?? 'AI'} is generating a response...` : 'Generating response', done: thinkingReceived, active: enrichedReceived && !thinkingReceived },
                ]
                const visibleSteps = enrichedReceived
                  ? [steps[0]!, { icon: '✅', label: `Analysed · Categories assigned · ${selectedAgentName ?? 'Agent'} selected`, done: true, active: false }, steps[4]!]
                  : [steps[0]!, steps[1]!, steps[3]!, steps[4]!]
                return visibleSteps.map(({ label, done, active, icon }) => (
                  <div key={label} className={s.aiStepRow}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: done ? '10px' : '8px', background: done ? 'rgba(124,58,237,0.15)' : active ? 'rgba(124,58,237,0.08)' : 'var(--bg-elevated)', border: `1.5px solid ${done ? 'var(--accent-primary)' : active ? 'var(--accent-primary)' : 'var(--border)'}`, color: 'var(--accent-primary)', transition: 'all 0.3s' }}>
                      {done ? '✓' : active ? <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--accent-primary)', animation: 'activePulse 0.9s ease-in-out infinite' }} /> : null}
                    </div>
                    <span style={{ fontSize: '0.78rem', fontWeight: active ? 500 : 400, color: done ? 'var(--text-secondary)' : active ? 'var(--text-primary)' : 'var(--text-muted)', transition: 'color 0.3s', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <span style={{ fontSize: '0.75rem', opacity: done ? 0.7 : 1 }}>{icon}</span>
                      {label}
                    </span>
                    {active && <span style={{ fontSize: '0.68rem', color: 'var(--accent-primary)', opacity: 0.7, animation: 'ellipsisDot 1.4s steps(4, end) infinite' }}>…</span>}
                  </div>
                ))
              })()}
              <style>{`
                @keyframes activePulse { 0%,100%{transform:scale(0.6);opacity:0.5} 50%{transform:scale(1.2);opacity:1} }
                @keyframes ellipsisDot { 0%{content:'.'} 33%{content:'..'} 66%{content:'...'} 100%{content:''} }
              `}</style>
            </div>
          </div>
        ) : (
          <div className={s.emptyComments}>
            <div className={s.emptyCommentsIcon}>💬</div>
            <div className={s.emptyCommentsText}>No replies yet — be the first!</div>
            <div className={s.emptyCommentsSub}>AI agent will respond automatically</div>
          </div>
        )
      )}

      {!commentsLoading && (
        <div className={s.composerCard}>
          {aiThinking.filter(t => t.parentCommentId === null).map(t => {
            const streamingText = streamingComments.get(t.agentId)
            const isStreaming = streamingText !== undefined && streamingText.length > 0
            return (
              <div key={t.agentId}>
                {isStreaming ? (
                  <div className={s.commentCardBg} style={{ background: COLOR_AI_BG, padding: '0.65rem 0.75rem', border: `1px solid ${COLOR_AI_BORDER}`, borderRadius: '8px', marginBottom: '0.15rem' }}>
                    <div className={s.commentAuthorRow}>
                      <div className={s.aiIconBox}>{t.agentIcon ?? '🤖'}</div>
                      <span style={{ fontWeight: 600, fontSize: '0.8rem', color: 'var(--accent-primary)' }}>{t.agentName}</span>
                      <span className={s.aiTagPill}>AI</span>
                    </div>
                    <div className={s.commentBody}>
                      {streamingText}<span style={{ display: 'inline-block', width: '2px', height: '1em', background: 'var(--accent-primary)', marginLeft: '1px', verticalAlign: 'text-bottom', animation: 'blink 0.8s step-end infinite' }} />
                      <style>{`@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
                    </div>
                  </div>
                ) : (
                  <div className={s.aiThinkingInner}>
                    <div className={s.aiIconBox}>{t.agentIcon ?? '🤖'}</div>
                    <span className={s.aiThinkingName}>{t.agentName}</span>
                    <span className={s.aiTagPill}>AI</span>
                    <div className={s.aiThinkingDotsRow}>
                      {[0, 1, 2].map(i => <div key={i} style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--accent-primary)', opacity: 0.7, animation: `aiDot 1.2s ease-in-out ${i * 0.2}s infinite` }} />)}
                    </div>
                    <style>{`@keyframes aiDot { 0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; } 40% { transform: scale(1); opacity: 1; } }`}</style>
                  </div>
                )}
                <div className={s.commentDivider} />
              </div>
            )
          })}
          {comments.map((c, i) => (
            <div key={c.id}>
              {i > 0 && <div className={s.commentDivider} />}
              <CommentCard comment={c} depth={0}
                isAuthenticated={isAuthenticated} accessToken={accessToken}
                discussionId={id!} currentUserId={user?.id}
                likedCommentIds={likedCommentIds}
                onPromptLogin={() => promptLogin('Sign in to interact')}
                onCountIncrement={() => setLiveCommentCount((prev) => (prev ?? (discussion?.commentCount ?? 0)) + 1)}
                aiThinking={aiThinking}
                streamingComments={streamingComments}
              />
            </div>
          ))}
        </div>
      )}

      <div ref={loaderRef} className={`${s.discSentinel} ${s.sentinelMargin}`}>
        {loadingMoreComments && <span className={s.loadMoreText}>Loading more...</span>}
      </div>

      {spendingCapModal && (
        <Modal onClose={() => setSpendingCapModal(null)} maxWidth="380px" style={{ textAlign: 'center' }}>
          <div className={s.spendingCapIcon}>⚡</div>
          <h3 className={s.spendingCapTitle}>{capConfig?.discussionModal.title ?? 'Daily AI limit reached'}</h3>
          <p className={s.spendingCapBody}>{capConfig?.discussionModal.body ?? "You've used all your free AI requests for today."}</p>
          <div className={s.spendingCapInfo}>
            <div className={s.spendingCapInfoRow}>
              <span className={s.spendingCapInfoLabel}>Resets at</span>
              <span className={s.spendingCapInfoValue}>
                {new Date(spendingCapModal.resetAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
              </span>
            </div>
          </div>
          <div className={s.spendingCapBtns}>
            <Button variant="secondary" fullWidth onClick={() => setSpendingCapModal(null)}>Got it</Button>
          </div>
          <p className={s.spendingCapNote}>{capConfig?.discussionModal.note ?? 'Free plan · Resets daily at midnight'}</p>
        </Modal>
      )}
    </div>
  )
}
